"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams } from "next/navigation"
import { useSupabase } from "@/lib/supabase/context"
import { Footer } from "@/components/footer"
import { LandingIntro } from "./components/landing-intro"
import { EmailGate } from "./components/email-gate"
import { ReviewForm } from "./components/review-form"
import { CompletionScreen } from "./components/completion-screen"
import { getDefaultReviewYear } from "./utils"
import posthog from "posthog-js"

function YearlyReviewContent() {
  const [hasStarted, setHasStarted] = useState(false)
  const [email, setEmail] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [showCompletion, setShowCompletion] = useState(false)
  const [reviewData, setReviewData] = useState<any>(null)
  const searchParams = useSearchParams()
  const supabase = useSupabase()

  // Check for referral code in URL
  useEffect(() => {
    const referralCode = searchParams.get('ref')
    if (referralCode) {
      // Store in localStorage for later use (when user signs up)
      localStorage.setItem('yearly_review_referral', referralCode)
    }
  }, [searchParams])

  // Check for email/year in URL (from access link)
  useEffect(() => {
    if (!isAuthenticated && !email) {
      const urlEmail = searchParams.get('email')
      if (urlEmail) {
        setEmail(urlEmail)
        localStorage.setItem('yearly_review_email', urlEmail)
        setHasStarted(true)
      }
    }
  }, [searchParams, isAuthenticated, email])

  // Anti-copying protection for titles
  useEffect(() => {
    // Disable common copy shortcuts on title elements
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && 
          (e.key === 'c' || e.key === 'a' || e.key === 'x')) {
        const target = e.target
        if (target && target instanceof HTMLElement && target.closest('.review-title')) {
          e.preventDefault()
          return false
        }
      }
    }

    // Disable right-click on titles
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target
      if (target && target instanceof HTMLElement && target.closest('.review-title')) {
        e.preventDefault()
        return false
      }
    }

    // Disable drag selection
    const handleSelectStart = (e: Event) => {
      const target = e.target
      if (target && target instanceof HTMLElement && target.closest('.review-title')) {
        e.preventDefault()
        return false
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('selectstart', handleSelectStart)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('selectstart', handleSelectStart)
    }
  }, [])

  // Check authentication status - silently handle missing sessions
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser()
        
        // Silently handle missing sessions - completely normal for unauthenticated users
        if (error) {
          const errorMessage = error.message || ''
          if (errorMessage.includes('Invalid Refresh Token') || 
              errorMessage.includes('Refresh Token Not Found') ||
              errorMessage.includes('refresh token') ||
              errorMessage.includes('Auth session missing') ||
              errorMessage.includes('AuthSessionMissingError')) {
            setIsAuthenticated(false)
            return
          }
        }
        
        if (user) {
          setIsAuthenticated(true)
          setUserId(user.id)
          setEmail(user.email || null)
        } else {
          setIsAuthenticated(false)
        }
      } catch (error: any) {
        // Silently handle expected "no session" errors
        const errorMessage = error?.message || ''
        if (errorMessage.includes('Invalid Refresh Token') || 
            errorMessage.includes('Refresh Token Not Found') ||
            errorMessage.includes('refresh token') ||
            errorMessage.includes('Auth session missing') ||
            errorMessage.includes('AuthSessionMissingError')) {
          setIsAuthenticated(false)
        }
      }
    }
    checkAuth()
  }, [supabase])

  // Load email from localStorage if available (for anonymous users)
  // Only load if not authenticated and email not already set
  useEffect(() => {
    if (!isAuthenticated && !email) {
      const storedEmail = localStorage.getItem('yearly_review_email')
      if (storedEmail) {
        setEmail(storedEmail)
        // If email exists, user has already started
        setHasStarted(true)
      }
    }
  }, [email, isAuthenticated])

  // If authenticated user has email, they've started
  useEffect(() => {
    if (isAuthenticated && email) {
      setHasStarted(true)
    }
  }, [isAuthenticated, email])

  const handleBegin = () => {
    setHasStarted(true)
    posthog.capture("yearly_review_started", { authenticated: isAuthenticated, year: getDefaultReviewYear() })
  }

  const handleEmailSubmit = (submittedEmail: string) => {
    setEmail(submittedEmail)
    localStorage.setItem('yearly_review_email', submittedEmail)
    posthog.capture("yearly_review_email_submitted", { year: getDefaultReviewYear() })
  }

  const handleReviewComplete = (data: any) => {
    setReviewData(data)
    setShowCompletion(true)
    posthog.capture("yearly_review_completed", { authenticated: isAuthenticated, year: getDefaultReviewYear() })
  }

  const currentYear = getDefaultReviewYear()

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {!hasStarted ? (
          <LandingIntro onBegin={handleBegin} />
        ) : !email ? (
          <div className="max-w-2xl mx-auto">
            <EmailGate onSubmit={handleEmailSubmit} />
          </div>
        ) : showCompletion ? (
          <CompletionScreen
            email={email!}
            userId={userId}
            reviewData={reviewData}
            onBack={() => setShowCompletion(false)}
          />
        ) : (
          <ReviewForm
            email={email!}
            userId={userId}
            onComplete={handleReviewComplete}
          />
        )}
      </div>
      
      <Footer user={null} />
    </div>
  )
}

export default function YearlyReviewPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>}>
      <YearlyReviewContent />
    </Suspense>
  )
}
